import {
  IssueCreationSource,
  IssueType,
  MediaType,
  PickerMode,
  useCurrentWorkspace,
} from '@gripp/shared-logic';
import {
  Button,
  Colors,
  Icon,
  NewIssue as NewIssueBase,
  Spacing,
  TopModalBar,
  tokens,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { useCallback } from 'react';
import { Dimensions, Modal, Pressable, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { MediaPicker } from '../../shared/components/media/mediaPicker';

export type NewIssueModalProps = {
  assetId?: string;
  workspaceId?: string;
  isOpened: boolean;
  onComplete: () => void;
  onIssueCreated?: (issueId: string, type?: IssueType) => void;
  onCancel?: () => void;
  modalType: 'sheet' | 'modal';
  acceptedMediaTypes?: MediaType[];
  hideAdvancedSettings?: boolean;
  createdFrom?: IssueCreationSource;
  pickerMode?: 'picker' | 'camera';
  selectedLabels?: Array<{ id: string; name: string }>;
  onLabelsChange?: (labels: Array<{ id: string; name: string }>) => void;
  onSelectorEdit?: () => void;
};

export const NewIssueModal = (props: NewIssueModalProps) => {
  return <NewIssueModalBase {...props} />;
};

export const NewIssueModalBase = (props: NewIssueModalProps) => {
  const onComplete = () => {
    props.onComplete();
  };

  const onCancel = () => {
    if (!props.onCancel) {
      onComplete();
    } else {
      props.onCancel();
    }
  };

  return (
    <Modal
      presentationStyle={props.modalType === 'sheet' ? 'pageSheet' : undefined}
      transparent
      visible={props.isOpened}
      onRequestClose={props.onCancel}
    >
      <View style={getOuterContainerStyle(props.modalType)}>
        <NewIssueBody {...props} onComplete={onComplete} onCancel={onCancel} />
      </View>
    </Modal>
  );
};

export const NewIssueBody = (props: NewIssueModalProps) => {
  const { t } = useTranslation();
  const currentWorkspace = useCurrentWorkspace();
  const slideLayout = useSlideLayoutSafe();
  const getWorkspaceId = useCallback(() => {
    return props.workspaceId ?? currentWorkspace?.id;
  }, [props.workspaceId, currentWorkspace]);

  return (
    <View style={getInnerContainerStyle(props.modalType)}>
      {props.onCancel && (
        <Pressable
          onPress={props.onCancel}
          style={styles.closeButton}
          accessibilityRole="button"
          accessibilityLabel={t('topNav.cancel')}
        >
          <Icon name="x" size={20} color={tokens.colors.neutral[800]} />
        </Pressable>
      )}
      <NewIssueBase
        assetId={props.assetId as string}
        workspaceId={getWorkspaceId()}
        mediaPicker={MediaPicker}
        acceptedMediaTypes={props.acceptedMediaTypes}
        onIssueCreated={(id: string, type?: IssueType) => {
          props.onIssueCreated?.(id, type);
          props.onComplete();
        }}
        topBar={TopModalBar}
        bottomBar={(barProps) => (
          <View style={styles.footer}>
            <Button
              style={styles.saveButton}
              onPress={!barProps.rightPressDisabled ? barProps.onSave : undefined}
              state={!barProps.rightPressDisabled ? 'active' : 'default'}
            >
              {barProps.saveLabel}
            </Button>
          </View>
        )}
        mode={PickerMode.PICKER}
        onCancel={props.onCancel}
        hideAdvancedSettings={props.hideAdvancedSettings}
        createdFrom={props.createdFrom}
        pickerMode={props.pickerMode}
        onSelectorEdit={props.onSelectorEdit ?? slideLayout?.toggleThirdPanel}
        selectedLabels={props.selectedLabels}
        onLabelsChange={props.onLabelsChange}
      />
    </View>
  );
};

const getOuterContainerStyle = (modalType: string) => {
  return modalType === 'sheet' ? styles.sheetContainer : styles.modalContainer;
};

const getInnerContainerStyle = (modalType: string) => {
  return modalType === 'sheet'
    ? styles.sheetInnerContainer
    : styles.innerContainer;
};

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: Dimensions.get('window').height / 7,
    backgroundColor: Colors.brand.white,
    zIndex: 1, // Ensure proper layering for dropdowns
  },
  innerContainer: {
    width: '100%',
    maxWidth: 560,
    height: 700, // Fixed height for proper scrolling
    borderRadius: 12,
    overflow: 'visible', // Allow dropdowns to render outside container
    zIndex: 1, // Ensure proper layering for dropdowns
  },
  sheetContainer: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  sheetInnerContainer: {
    width: '100%',
    height: '100%',
    maxHeight: '100%',
  },
  footer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: Spacing.baseMargin.base,
    paddingVertical: Spacing.baseMargin.base,
    backgroundColor: 'transparent',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  saveButton: {
    marginBottom: Spacing.baseMargin.base,
    borderRadius: 2,
    width: '100%',
    height: 48,
  },
  closeButton: {
    position: 'absolute',
    top: tokens.spacing.md,
    right: tokens.spacing.md,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: tokens.colors.neutral[100],
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
});
